import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, Alert, Chip } from "@mui/material";
import { useAuth } from "../hooks/useAuth";
import AdminLayout from "./AdminLayout";
import creditService from "../services/creditService";
import { db } from "../firebase/config";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { canShowUnlockButton } from "../utils/onboarding";

const REQUIRED = 480;

export default function DashboardUnlockPage() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      if (!user?.uid) return;
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const total = snap.exists() ? (snap.data().totalCredits || 0) : 0;
        if (!active) return;
        setCredits(total);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [user]);

  const handleUnlock = async () => {
    setError("");
    setSuccess("");
    try {
      await creditService.spendCreditsForUnlock(user.uid, REQUIRED);
      setSuccess("Dashboard unlocked successfully.");
      navigate(userProfile?.role === "startup" ? "/startup" : "/student", { replace: true });
    } catch (e) {
      setError(e.message || "Unlock failed");
    }
  };

  return (
    <AdminLayout title="Unlock Dashboard">
      <Box sx={{ p: 3 }}>
        <Card sx={{ maxWidth: 720, mx: "auto", borderTop: "6px solid #C1272D" }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#003366", mb: 1 }}>
              Unlock Your Dashboard
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              You need {REQUIRED} credits to unlock. This amount will be deducted from your total.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#003366" }}>
                Your Credits:
              </Typography>
              <Chip label={loading ? "Loading..." : credits} color="primary" />
            </Box>

            {canShowUnlockButton(credits, userProfile?.dashboardUnlocked, REQUIRED) && (
              <Button
                variant="contained"
                disabled={loading}
                onClick={handleUnlock}
                sx={{
                  px: 4,
                  background: "linear-gradient(135deg,#003366,#004C99)",
                  "&:hover": { background: "linear-gradient(135deg,#00264d,#003d7a)" }
                }}
              >
                Spend {REQUIRED} Credits & Unlock
              </Button>
            )}
            {credits < REQUIRED && (
              <Alert severity="info">
                You currently have {credits} credits. Earn at least {REQUIRED} credits to unlock your dashboard.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    </AdminLayout>
  );
}
